import React from 'react';
import useTranslation from 'hooks/use-translation';
import { Nullish } from 'shared/utils/type-utils';
import { CircleBankLogo } from 'src/payments/components/circle-bank-logo';
import { UNKNOWN_BANK, BANK_DIGITS_MISSING, PAYMENT_INFORMATION, ERROR_STATE } from 'src/payments/constants';
import { BankActions } from 'src/payments/components/bank-actions';
import {
  BankInfo,
  BankName,
  BankText,
  Content,
  DotSeparator,
  PaymentCard,
  Section,
  Title,
} from './payment-information.styles';

export type PaymentInformationProps = {
  bankInformation?: {
    bankName?: Nullish | string;
    lastDigits?: Nullish | string;
    bankLogo?: Nullish | string;
  };
  needsRelinkBankAccount: boolean;
  editBankAccount: () => void;
  relinkBankAccount: () => void;
  paymentComplete: boolean;
};

export const PaymentInformation = ({
  bankInformation,
  needsRelinkBankAccount,
  editBankAccount,
  relinkBankAccount,
  paymentComplete,
}: PaymentInformationProps): JSX.Element => {
  const { t } = useTranslation();
  const { bankLogo } = bankInformation ?? {};

  const bankName = bankInformation?.bankName ?? UNKNOWN_BANK;
  const lastDigits = bankInformation?.lastDigits ?? t('common.bankDigits.missing', BANK_DIGITS_MISSING);

  return (
    <Section data-testid='payment-information'>
      <Title>{t('common.paymentInformation', PAYMENT_INFORMATION)}:</Title>
      <PaymentCard
        error={needsRelinkBankAccount}
        aria-invalid={needsRelinkBankAccount ? 'true' : undefined}
        aria-label={needsRelinkBankAccount ? t('common.paymentInformation.errorState', ERROR_STATE) : undefined}
      >
        <Content>
          <BankInfo>
            <CircleBankLogo
              altText={`${bankName} Logo`}
              logo={bankLogo}
              circleDiameter={20}
              iconHeight={14}
              iconWidth={14}
            />
            <BankText>
              <BankName>{bankName}</BankName>
              <DotSeparator>‧</DotSeparator>
              {lastDigits}
            </BankText>
          </BankInfo>
          {!paymentComplete && (
            <BankActions
              bankNeedsRelinking={needsRelinkBankAccount}
              editBankAccount={editBankAccount}
              relinkBankAccount={relinkBankAccount}
            />
          )}
        </Content>
      </PaymentCard>
    </Section>
  );
};
